<template>
  <div class="stepper-wrapper">
    <div
      ref="track"
      class="stepper"
      @scroll="updateScrollShadows"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        class="stepper__node"
        :class="`stepper__node--${item.status}`"
      >
        <span class="stepper__line" />
        <span class="stepper__dot">
          <template v-if="item.status === 'done'">✓</template>
          <template v-else-if="item.status === 'current'">●</template>
        </span>
        <span class="stepper__name">{{ item.name }}</span>
        <span class="stepper__time">{{ itemTime(item) }}</span>
      </div>
    </div>
    <div
      class="stepper-wrapper__shadow stepper-wrapper__shadow--left"
      :class="{ 'stepper-wrapper__shadow--visible': canScrollLeft }"
    />
    <div
      class="stepper-wrapper__shadow stepper-wrapper__shadow--right"
      :class="{ 'stepper-wrapper__shadow--visible': canScrollRight }"
    />
  </div>
</template>

<script>
export default {
  name: 'BaseStepper',
  props: {
    items: {
      type: Array,
      required: true,
      validator: (items) => items.every((item) => ['done', 'current', 'future'].includes(item.status))
    }
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false
    }
  },
  watch: {
    items() {
      this.$nextTick(this.updateScrollShadows)
    }
  },
  mounted() {
    this.updateScrollShadows()
    window.addEventListener('resize', this.updateScrollShadows)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollShadows)
  },
  methods: {
    itemTime(item) {
      if (item.status === 'future') {
        return '—'
      }
      if (item.status === 'current') {
        return `chegou ${item.timestamp}`
      }
      return item.timestamp
    },
    updateScrollShadows() {
      const track = this.$refs.track
      if (!track) {
        return
      }
      this.canScrollLeft = track.scrollLeft > 0
      this.canScrollRight = track.scrollLeft + track.clientWidth < track.scrollWidth - 1
    }
  }
}
</script>

<style lang="scss">
.stepper-wrapper {
  position: relative;

  &__shadow {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 32px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
    z-index: 3;

    &--visible {
      opacity: 1;
    }

    &--left {
      left: 0;
      background: linear-gradient(to right, rgba(0, 0, 0, 0.18), transparent);
    }

    &--right {
      right: 0;
      background: linear-gradient(to left, rgba(0, 0, 0, 0.18), transparent);
    }
  }
}

.stepper {
  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  padding: 4px 2px;

  &__node {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    min-width: 88px;
    position: relative;

    &:first-child .stepper__line {
      display: none;
    }

    &--done .stepper__line,
    &--current .stepper__line {
      background: var(--v-success-base);
    }

    &--done .stepper__dot {
      background: var(--v-success-base);
      border-color: var(--v-success-base);
      color: #fff;
    }

    &--current .stepper__dot {
      border-color: var(--v-primary-base);
      color: var(--v-primary-base);
      box-shadow: 0 0 0 4px rgba(64, 117, 109, 0.14);
    }

    &--future .stepper__name {
      color: var(--ds-color-black-040);
      font-weight: 500;
    }

    &--current .stepper__name {
      color: var(--v-primary-base);
    }

    &--current .stepper__time {
      color: var(--v-primary-base);
      font-weight: 600;
    }
  }

  &__line {
    position: absolute;
    top: 11px;
    left: -50%;
    width: 100%;
    height: 2px;
    background: var(--ds-color-black-010);
    z-index: 1;
  }

  &__dot {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    background: var(--ds-color-white);
    border: 2px solid var(--ds-color-black-010);
    color: var(--ds-color-black-040);
    z-index: 2;
  }

  &__name {
    font-size: 0.8rem;
    font-weight: 600;
    margin-top: 8px;
    text-align: center;
    color: var(--v-textPrimary-base);
  }

  &__time {
    font-size: 0.75rem;
    color: var(--ds-color-black-040);
    margin-top: 2px;
    text-align: center;
  }
}
</style>
